'use client';

import { UploadCloud } from 'lucide-react';

import {
  ChangeEvent,
  DragEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Link from 'next/link';

import { SUBTITLE_GENERATOR_FAQS } from './faqs';
import { MAX_AUDIO_BYTES, isAllowedAudioExtension } from '@engine/tools-shell';

import {
  completePublicUpload,
  initPublicUpload,
  uploadPublicChunks,
} from '@/lib/free-tools/public-chunked-uploader';
import { Badge } from '@/components/badge';
import {
  FreeToolTranscribeCta,
  trackFreeToolTranscribeIntent,
  useFreeToolTranscribeHandoffLinks,
} from '@/components/free-tools/free-tool-transcribe-cta';
import { Progress } from '@/components/progress';
import { cn } from '@/lib/utils';

type JobStatus = 'queued' | 'pending' | 'processing' | 'completed' | 'failed';

type SubtitleFormat = 'srt' | 'vtt' | 'txt';

interface SubmitResponse {
  success: boolean;
  data?: { jobId: string; publicToken: string; handoffToken?: string };
  error?: string;
}

interface JobResponse {
  success: boolean;
  data?: {
    id: string;
    status: JobStatus;
    fileName: string;
    transcriptText: string;
    durationSeconds: number;
    wordCount: number;
    errorMessage: string | null;
  };
  error?: string;
}

const ACCEPT = '.mp4,.mp3,.wav,.m4a,.ogg,.webm,.flac,.aac,.mpeg,.mpga';

const BLOCKED_MIME_PREFIXES = ['image/'];

/** 'sentence' = classic cues; 1-3 = short TikTok/Reels-style cues (SRT/VTT only). */
type CaptionLength = 'sentence' | '1' | '2' | '3';

const CAPTION_LENGTHS: Array<{ key: CaptionLength; label: string }> = [
  { key: 'sentence', label: 'Full sentences' },
  { key: '1', label: '1 word' },
  { key: '2', label: '2 words' },
  { key: '3', label: '3 words' },
];

const FORMATS: Array<{ key: SubtitleFormat; label: string; hint: string }> = [
  { key: 'srt', label: 'SRT', hint: 'YouTube, Premiere, CapCut' },
  { key: 'vtt', label: 'VTT', hint: 'Web video / HTML5' },
  { key: 'txt', label: 'TXT', hint: 'Timestamped plain text' },
];

function getFriendlySubmitError(message: string | null | undefined): string {
  if (!message) return 'We could not generate subtitles right now. Please try again.';
  const lower = message.toLowerCase();

  if (
    lower.includes('unexpected end of form') ||
    lower.includes('multipart') ||
    lower.includes('content-type')
  ) {
    return 'Upload was interrupted. Please reselect your file and try again.';
  }

  if (lower.includes('unsupported file type')) {
    return 'Unsupported file type. Use MP4, MP3, WAV, M4A, OGG, WEBM, FLAC, AAC, or MPEG.';
  }

  if (lower.includes('500mb') || lower.includes('file size')) {
    return 'File size exceeds 500MB free limit.';
  }

  if (lower.includes('no file was received') || lower.includes('no file was fully uploaded')) {
    return 'No file was fully uploaded. Please reselect your file and try again.';
  }

  if (lower.includes('session expired')) {
    return 'Upload session expired. Please reselect your file and try again.';
  }

  return message;
}

export function SubtitleGeneratorClient() {
  const [file, setFile] = useState<File | null>(null);
  const [inputUrl, setInputUrl] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [publicToken, setPublicToken] = useState<string | null>(null);
  const [handoffToken, setHandoffToken] = useState<string | null>(null);
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [wordCount, setWordCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [format, setFormat] = useState<SubtitleFormat>('srt');
  const [captionLength, setCaptionLength] = useState<CaptionLength>('sentence');
  const [preview, setPreview] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const progressValue = useMemo(() => {
    if (status === 'queued') return 10;

    if (status === 'pending') return 20;

    if (status === 'processing' && uploadProgress > 0 && uploadProgress < 100) {
      return Math.max(20, Math.min(60, Math.round(uploadProgress * 0.6)));
    }

    if (status === 'processing') return 65;

    if (status === 'completed') return 100;

    if (status === 'failed') return 0;

    return 0;
  }, [status, uploadProgress]);

  const canSubmit = Boolean(file || inputUrl.trim().length > 0) && !submitting;
  const isReady = status === 'completed' && Boolean(jobId && publicToken);
  const showUploader = inputUrl.trim().length === 0;
  const showLinkInput = !file;

  const { primaryHref: transcribeHandoffHref, ready: handoffLinksReady } =
    useFreeToolTranscribeHandoffLinks(jobId, handoffToken);

  function subtitleUrl(fmt: SubtitleFormat, len: CaptionLength = captionLength): string {
    const short = fmt !== 'txt' && len !== 'sentence' ? `&words_per_caption=${len}` : '';

    return `/api/tools/free-audio-to-text/jobs/${jobId}/subtitles?format=${fmt}&token=${encodeURIComponent(
      publicToken ?? ''
    )}${short}`;
  }

  const loadPreview = useCallback(
    async (fmt: SubtitleFormat, len: CaptionLength) => {
      if (!jobId || !publicToken) return;
      setPreviewLoading(true);
      setCopyState('idle');

      try {
        const res = await fetch(subtitleUrl(fmt, len), { cache: 'no-store' });
        const text = await res.text();
        setPreview(res.ok ? text : 'Could not load subtitles. Please try downloading instead.');
      } catch {
        setPreview('Could not load subtitles. Please try downloading instead.');
      } finally {
        setPreviewLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobId, publicToken]
  );

  // Load the default preview once the job completes.
  useEffect(() => {
    if (isReady) {
      void loadPreview(format, captionLength);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  function isAllowedAudioFile(nextFile: File): boolean {
    if (!isAllowedAudioExtension(nextFile.name)) return false;
    const mimeType = nextFile.type.toLowerCase();

    if (BLOCKED_MIME_PREFIXES.some((prefix) => mimeType.startsWith(prefix))) return false;

    return true;
  }

  function handleSelectFile(nextFile: File | null): void {
    if (nextFile && !isAllowedAudioFile(nextFile)) {
      setFile(null);
      setError('Unsupported file type. Use MP4, MP3, WAV, M4A, OGG, WEBM, FLAC, AAC, or MPEG.');

      return;
    }

    if (nextFile && nextFile.size > MAX_AUDIO_BYTES) {
      setFile(null);
      setError('File size exceeds 500MB free limit.');

      return;
    }

    setFile(nextFile);

    if (nextFile) {
      setError(null);
      setInputUrl('');
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
    handleSelectFile(event.target.files?.[0] ?? null);
  }

  function openFilePicker(): void {
    fileInputRef.current?.click();
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragActive(false);
    const droppedFile = event.dataTransfer.files?.[0] ?? null;

    if (droppedFile) handleSelectFile(droppedFile);
  }

  function handleLinkChange(event: ChangeEvent<HTMLInputElement>): void {
    const nextUrl = event.target.value;
    setInputUrl(nextUrl);

    if (nextUrl.trim().length > 0) {
      setError(null);
      setFile(null);

      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function handleSelectFormat(fmt: SubtitleFormat): void {
    setFormat(fmt);
    void loadPreview(fmt, captionLength);
  }

  function handleSelectCaptionLength(len: CaptionLength): void {
    setCaptionLength(len);
    void loadPreview(format, len);
  }

  async function handleCopy(): Promise<void> {
    if (!preview) return;

    try {
      await navigator.clipboard.writeText(preview);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('idle');
    }
  }

  async function pollJob(nextJobId: string, token: string): Promise<void> {
    for (let i = 0; i < 120; i += 1) {
      const response = await fetch(
        `/api/tools/free-audio-to-text/jobs/${nextJobId}?token=${encodeURIComponent(token)}`,
        { method: 'GET', cache: 'no-store' }
      );

      // SAFETY: The surrounding typed contract establishes the asserted representation.
      const payload = (await response.json()) as JobResponse;

      if (!response.ok || !payload.success || !payload.data) {
        setError(payload.error ?? 'Failed to fetch subtitle status');
        setStatus('failed');

        return;
      }

      setStatus(payload.data.status);
      setWordCount(payload.data.wordCount || 0);
      setError(payload.data.errorMessage ?? null);

      if (payload.data.status === 'completed' || payload.data.status === 'failed') return;
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    setStatus('failed');
    setError('Subtitle generation took too long. Please try again.');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    setStatus('pending');
    setWordCount(0);
    setPreview('');
    setFormat('srt');
    setCaptionLength('sentence');
    setCopyState('idle');
    setJobId(null);
    setPublicToken(null);
    setHandoffToken(null);
    setUploadProgress(0);

    try {
      let submitData: SubmitResponse['data'] | undefined;

      if (file) {
        const session = await initPublicUpload(file);
        await uploadPublicChunks(file, session, setUploadProgress);
        submitData = await completePublicUpload(session);
      } else {
        const formData = new FormData();

        if (inputUrl.trim()) formData.set('inputUrl', inputUrl.trim());

        const response = await fetch('/api/tools/free-audio-to-text/jobs', {
          method: 'POST',
          body: formData,
        });

        // SAFETY: The surrounding typed contract establishes the asserted representation.
        const payload = (await response.json()) as SubmitResponse;

        if (!response.ok || !payload.success || !payload.data) {
          setStatus('failed');
          setError(getFriendlySubmitError(payload.error));

          return;
        }

        submitData = payload.data;
      }

      if (!submitData) {
        setStatus('failed');
        setError('We could not generate subtitles right now. Please try again.');

        return;
      }

      setJobId(submitData.jobId);
      setPublicToken(submitData.publicToken);
      setHandoffToken(submitData.handoffToken ?? submitData.publicToken);
      setStatus('processing');
      await pollJob(submitData.jobId, submitData.publicToken);
    } catch (submitError) {
      setStatus('failed');
      setError(
        getFriendlySubmitError(
          submitError instanceof Error ? submitError.message : 'Unexpected error'
        )
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleStartOver(): void {
    setFile(null);
    setInputUrl('');
    setIsDragActive(false);
    setJobId(null);
    setPublicToken(null);
    setHandoffToken(null);
    setStatus(null);
    setWordCount(0);
    setError(null);
    setSubmitting(false);
    setPreview('');
    setFormat('srt');
    setCaptionLength('sentence');
    setCopyState('idle');
    setUploadProgress(0);

    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div className="space-y-6">
      {/* H1 and intro live in FreeToolPageShell; only live status stays here. */}

      {!isReady ? (
        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-[28px] border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5 sm:p-6"
        >
          {showUploader ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium" htmlFor="subtitle-file">
                Upload audio or video file
              </label>
              <input
                id="subtitle-file"
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                onChange={handleFileChange}
                className="sr-only"
              />
              <div
                role="button"
                tabIndex={0}
                onClick={openFilePicker}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openFilePicker();
                  }
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={handleDrop}
                className={cn(
                  'flex min-h-36 w-full cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-4 py-5 text-center transition-colors',
                  isDragActive
                    ? 'border-violet-400/70 bg-violet-500/10'
                    : 'border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] hover:border-violet-400/70 hover:bg-[rgb(var(--surface-hover))]'
                )}
              >
                <UploadCloud aria-hidden className="mb-3 h-7 w-7 text-[#C4B5FD]" />
                <p className="text-base font-semibold text-[rgb(var(--foreground))]">
                  Drag and drop your file
                </p>
                <p className="mt-1 text-sm text-[rgb(var(--foreground-secondary))]">
                  or click to browse files
                </p>
                <p className="mt-4 text-xs text-[rgb(var(--foreground-secondary))]">
                  Supported: MP4, MP3, WAV, M4A, OGG, WEBM, FLAC, AAC, MPEG. Max 10 minutes free /
                  500MB.
                </p>
              </div>
            </div>
          ) : null}

          {file ? (
            <div className="rounded-lg border border-violet-400/25 bg-[rgb(var(--surface))] p-3">
              <p className="text-xs text-[rgb(var(--foreground-secondary))]">Selected file</p>
              <p className="mt-1 break-all text-sm font-medium text-[rgb(var(--foreground))]">
                {file.name}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={openFilePicker}
                  className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/15"
                >
                  Change file
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);

                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/15"
                >
                  Clear
                </button>
              </div>
            </div>
          ) : null}

          {showLinkInput ? (
            <details className="pt-2">
              <summary className="cursor-pointer text-sm font-medium text-[rgb(var(--foreground-secondary))]">
                Use a link instead
              </summary>
              <label className="block text-sm font-medium" htmlFor="subtitle-link">
                Or paste a YouTube or direct media link
              </label>
              <input
                id="subtitle-link"
                type="url"
                placeholder="https://youtube.com/watch?v=... or https://example.com/file.mp3"
                value={inputUrl}
                onChange={handleLinkChange}
                className="mt-2 w-full rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] px-4 py-3 text-sm"
              />
            </details>
          ) : null}

          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-3 inline-flex items-center justify-center rounded-full bg-[rgb(var(--tool-cta))] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[rgb(var(--tool-cta-hover))] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Generating subtitles...' : 'Generate Subtitles'}
          </button>
        </form>
      ) : null}
      <details className="text-sm text-[rgb(var(--foreground-secondary))]">
        <summary className="mx-auto w-fit cursor-pointer">About this tool</summary>
        <div className="space-y-4">
          <p className="text-[rgb(var(--foreground-secondary))]">
            Generate subtitles free online. Upload audio or video and download timestamped SRT, VTT,
            or TXT captions in minutes. No signup required for files up to 10 minutes.
          </p>
          <p className="text-sm text-[rgb(var(--foreground-secondary))]">
            Need longer files, speaker labels, or editable captions? Continue in{' '}
            <Link href="/features" className="underline underline-offset-2">
              Wisprs features
            </Link>{' '}
            or compare plans on{' '}
            <Link href="/pricing" className="underline underline-offset-2">
              pricing
            </Link>
            .
          </p>
        </div>
      </details>

      {status && !isReady ? (
        <div className="mt-6 rounded-xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5">
          {status !== 'failed' ? (
            <Progress value={progressValue} showLabel className="mb-3" />
          ) : null}
          {status === 'failed' ? (
            <div className="space-y-1">
              <p className="text-sm font-semibold text-[rgb(var(--error))]">
                Subtitles could not be generated
              </p>
              <p className="text-sm text-[rgb(var(--foreground-secondary))]">
                {error ?? 'Please try again.'}
              </p>
              <p className="text-xs text-[rgb(var(--foreground-secondary))]">
                Free mode supports audio/video files up to 10 minutes. Try reselecting the file and
                submitting again.
              </p>
            </div>
          ) : (
            <p className="text-sm text-[rgb(var(--foreground-secondary))]">
              {status === 'queued' && 'Upload queued. Waiting for worker...'}
              {status === 'pending' && 'Upload queued. Preparing transcription...'}
              {status === 'processing' && 'Transcribing and timing your subtitles...'}
            </p>
          )}
          {jobId ? (
            <p className="mt-2 font-mono text-[11px] text-[rgb(var(--foreground-secondary))]">
              Job {jobId}
            </p>
          ) : null}
        </div>
      ) : null}

      {isReady ? (
        <div className="mt-8 space-y-4 rounded-xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Your subtitles are ready</h2>
            <p className="text-xs text-[rgb(var(--foreground-secondary))]">{wordCount} words</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {FORMATS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => handleSelectFormat(f.key)}
                className={cn(
                  'inline-flex flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors',
                  format === f.key
                    ? 'border-violet-400/70 bg-violet-500/10'
                    : 'border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] hover:border-amber-400/45'
                )}
              >
                <span className="text-sm font-semibold">{f.label}</span>
                <span className="text-[11px] text-[rgb(var(--foreground-secondary))]">
                  {f.hint}
                </span>
              </button>
            ))}
          </div>

          {format !== 'txt' ? (
            <div
              className="flex flex-wrap items-center gap-2"
              role="group"
              aria-label="Caption length"
            >
              <span className="text-xs font-medium text-[rgb(var(--foreground-secondary))]">
                Caption length
              </span>
              {CAPTION_LENGTHS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  aria-pressed={captionLength === c.key}
                  onClick={() => handleSelectCaptionLength(c.key)}
                  className={cn(
                    'min-h-9 rounded-full border px-3 text-xs font-semibold transition-colors',
                    captionLength === c.key
                      ? 'border-violet-400/70 bg-violet-500/10'
                      : 'border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] hover:border-amber-400/45'
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          ) : null}

          <textarea
            readOnly
            value={previewLoading ? 'Loading…' : preview}
            rows={12}
            className="min-h-72 max-h-[60vh] w-full resize-y rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] p-4 font-mono text-xs leading-5"
          />

          <div className="flex flex-wrap gap-3">
            <a
              href={subtitleUrl(format)}
              download
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/20 bg-white/10 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/15"
            >
              Download .{format}
            </a>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/20 bg-white/10 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/15"
            >
              {copyState === 'copied' ? 'Copied' : `Copy ${format.toUpperCase()}`}
            </button>
            <button
              type="button"
              onClick={handleStartOver}
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/20 bg-white/10 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/15"
            >
              Start over
            </button>
          </div>

          <p className="text-sm text-[rgb(var(--foreground-secondary))]">
            Want speaker-labeled captions, longer files, or editable subtitle timing? Import this
            into Wisprs.
          </p>
          <div className="flex flex-wrap gap-3">
            <FreeToolTranscribeCta
              jobId={jobId}
              downloadToken={handoffToken}
              freeToolKind="free-audio-to-text"
              className="w-auto px-5"
            >
              Edit &amp; Export on Wisprs
            </FreeToolTranscribeCta>
          </div>
          <p className="text-xs text-[rgb(var(--foreground-secondary))]">
            Already have an account?{' '}
            <Link
              href={handoffLinksReady ? transcribeHandoffHref : '/sign-in'}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[rgb(var(--foreground))] underline underline-offset-2"
              onClick={() => {
                if (jobId && handoffLinksReady) {
                  trackFreeToolTranscribeIntent('free-audio-to-text', jobId);
                }
              }}
            >
              Import this transcript
            </Link>{' '}
            to continue in the dashboard.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Long-form SEO cards for this tool. Rendered by the page through FreeToolPageShell's `body`
 * slot, below the hero, so the tool, its "About"/"How this works" panel and the platform
 * chips stay together in the first view.
 */
export function SubtitleGeneratorSeoSections() {
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-5">
          <h2 className="text-base font-semibold">How this free subtitle generator works</h2>
          <ol className="mt-3 list-decimal space-y-1 pl-4 text-sm text-[rgb(var(--foreground-secondary))]">
            <li>Upload an audio or video file, or paste a direct media URL.</li>
            <li>We transcribe and time each caption automatically.</li>
            <li>Download SRT, VTT, or TXT, or import into Wisprs to edit timing and speakers.</li>
          </ol>
        </section>
        <section className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-5">
          <h2 className="text-base font-semibold">SRT vs VTT vs TXT</h2>
          <p className="mt-3 text-sm text-[rgb(var(--foreground-secondary))]">
            <strong>SRT</strong> works in YouTube, Premiere, and CapCut. <strong>VTT</strong> is the
            web/HTML5 caption standard. <strong>TXT</strong> gives you timestamped plain text. For
            long-form video, speaker labels, and reusable captions, use{' '}
            <Link
              href="/use-cases/meeting-transcription-software"
              className="underline underline-offset-2"
            >
              meeting transcription software
            </Link>{' '}
            and{' '}
            <Link
              href="/podcast/podcast-transcription-service"
              className="underline underline-offset-2"
            >
              podcast transcription workflows
            </Link>
            .
          </p>
        </section>
      </div>
    </>
  );
}
